import { useState, useCallback, useRef } from 'react';
import { Message, ChatSession } from '../types';
import { useLanguage } from '../../../contexts/LanguageContext';

// API Configuration from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://pogo79jcg8.execute-api.us-east-1.amazonaws.com/prod/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

export const useChatSession = () => {
  const { language, t } = useLanguage();
  const [session, setSession] = useState<ChatSession>({
    sessionId: null,
    messages: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const audioCacheRef = useRef<Map<string, string>>(new Map());

  // Initialize session with welcome message
  const initializeSession = useCallback(() => {
    const welcomeMessage: Message = {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: t('widget.welcomeMessage'),
      timestamp: new Date(),
      isWelcome: true,
    };

    setSession({
      sessionId: `session-${Date.now()}`,
      messages: [welcomeMessage],
    });
  }, [t]);

  // Send a message and get response
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      setIsLoading(true);

      // Add user message
      const userMessage: Message = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };

      setSession(prev => ({
        ...prev,
        messages: [...prev.messages, userMessage],
      }));

      try {
        // Call real API
        console.log('Calling API:', API_BASE_URL);
        console.log('API Key:', API_KEY ? 'Present' : 'Missing');

        const response = await fetch(`${API_BASE_URL}/query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
          },
          body: JSON.stringify({
            query: content.trim(),
            session_id: session.sessionId,
            language: language, // Send user's preferred language
          }),
        });

        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Error Response:', errorText);
          throw new Error(`API request failed: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        console.log('API Response:', data);

        // Update session ID if new
        if (data.session_id && !session.sessionId) {
          setSession(prev => ({
            ...prev,
            sessionId: data.session_id,
          }));
        }

        // Add assistant message
        const assistantMessage: Message = {
          id: `msg-${Date.now() + 1}`,
          role: 'assistant',
          content: data.answer || data.response || 'No response from server',
          timestamp: new Date(),
          sources: data.sources,
          sourceLinks: data.source_links,
        };

        setSession(prev => ({
          ...prev,
          messages: [...prev.messages, assistantMessage],
          sessionId: data.session_id || prev.sessionId,
        }));
      } catch (error) {
        console.error('Error sending message:', error);

        // Add error message
        const errorMessage: Message = {
          id: `msg-${Date.now() + 1}`,
          role: 'assistant',
          content: t('widget.error'),
          timestamp: new Date(),
          isError: true,
        };

        setSession(prev => ({
          ...prev,
          messages: [...prev.messages, errorMessage],
        }));
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, t, session.sessionId, language]
  );

  // Clear session and start new
  const clearSession = useCallback(async () => {
    // Clear the current session on backend if exists
    if (session.sessionId) {
      try {
        await fetch(`${API_BASE_URL}/clear-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
          },
          body: JSON.stringify({
            session_id: session.sessionId,
          }),
        });
      } catch (error) {
        console.error('Error clearing session:', error);
        // Continue anyway to clear frontend state
      }
    }

    // Revoke cached audio URLs to free memory
    audioCacheRef.current.forEach(url => URL.revokeObjectURL(url));
    audioCacheRef.current.clear();

    // Clear frontend state and initialize new session
    initializeSession();
  }, [session.sessionId, initializeSession]);

  return {
    session,
    isLoading,
    sendMessage,
    clearSession,
    initializeSession,
    audioCacheRef,
  };
};
